import { useEffect, useRef, useState } from "react";
import {
  callOutcomes,
  customerTypes,
  dropdownValues,
  ensureDropdownValue,
  industryTypes,
  leadSources,
  leadStages,
  lifecycleStages,
  painPoints,
  visitOutcomes,
} from "../config/options.js";
import { customerOwnerOptions, hasActiveCustomerOwner } from "../lib/customerAccess.js";
import { getLocalCalendarDate } from "../lib/date.js";
import { CheckboxField, Modal, SelectField, TextField } from "./ui.jsx";

export function CustomerModal({
  user,
  users,
  customers,
  customer,
  dropdownOptions = [],
  canDraftOrder = false,
  onBack,
  backLabel,
  onClose,
  onSave,
  onAddEmployee,
  onOpenEmployee,
  onAddDropdownOption,
}) {
  // `users` is already access-scoped by the API, so managers can assign descendants too.
  const reps = users.filter((item) => item.role === "sales" && item.active !== false);
  const currentOwner = users.find((item) => item.id === customer?.ownerId);
  const activeOwnerOptions = reps.map((rep) => ({ value: rep.id, label: rep.name }));
  const ownerOptions = currentOwner?.active === false
    ? [{ value: currentOwner.id, label: `${currentOwner.name} (inactive)` }, ...activeOwnerOptions]
    : activeOwnerOptions;
  const displayedOwnerOptions = ownerOptions.length ? ownerOptions : [{ value: "", label: "No active sales reps available" }];
  const customerTypeOptions = dropdownValues(dropdownOptions, "customerType", customerTypes);
  const industryTypeOptions = dropdownValues(dropdownOptions, "industryType", industryTypes);
  const leadSourceOptions = dropdownValues(dropdownOptions, "leadSource", leadSources);
  const painPointOptions = dropdownValues(dropdownOptions, "painPoint", painPoints);
  const leadStatusOptions = dropdownValues(dropdownOptions, "leadStatus", leadStages);
  const lifecycleStageOptions = dropdownValues(dropdownOptions, "lifecycleStage", lifecycleStages);
  const defaultIndustryType = industryTypeOptions[0] || "Tiles";
  const [form, setForm] = useState(
    customer ? { customFields: {}, ...customer } : {
      ownerId: user.role === "sales" ? user.id : reps[0]?.id,
      name: "",
      phone: "",
      email: "",
      address: "",
      location: "",
      type: customerTypeOptions[0] || "B2B",
      productInterest: "",
      budget: "",
      potentialBudget: "",
      timeline: "",
      industryType: defaultIndustryType,
      businessVerticals: [defaultIndustryType],
      leadSource: leadSourceOptions[0] || "Field visit",
      decisionMaker: "",
      quantityRequirement: "",
      painPoint: painPointOptions[0] || "Price",
      competitorComparison: "",
      negotiationTerms: "",
      status: leadStatusOptions[0] || "New",
      lifecycleStage: lifecycleStageOptions[0] || "Initial",
      lifecycleReason: "Prospecting",
      latitude: "",
      longitude: "",
      geofenceRadiusMeters: 500,
      creditLimit: 250000,
      consentOptIn: true,
      followUpDate: "",
      notes: "",
      customFields: {},
    },
  );
  const [dropdownEditor, setDropdownEditor] = useState(null);
  const [customFieldDraft, setCustomFieldDraft] = useState({ label: "", value: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const submittingRef = useRef(false);

  const duplicate = customers.find((item) => item.phone === form.phone && item.id !== customer?.id);
  const hasActiveAssignableRep = reps.length > 0;
  const canSaveProspect = reps.some((rep) => rep.id === form.ownerId);
  const customFieldEntries = Object.entries(form.customFields || {});
  const repSignature = reps.map((rep) => rep.id).join("|");

  useEffect(() => {
    setForm((current) => {
      if (user.role === "sales") {
        return current.ownerId === user.id ? current : { ...current, ownerId: user.id };
      }
      if (!reps.length || reps.some((rep) => rep.id === current.ownerId)) return current;
      return { ...current, ownerId: reps[0].id };
    });
  }, [repSignature, user.id, user.role]);

  function addCustomField() {
    const label = customFieldDraft.label.trim();
    const value = customFieldDraft.value.trim();
    if (!label || !value) return;
    setForm((current) => ({
      ...current,
      customFields: {
        ...(current.customFields || {}),
        [label]: value,
      },
    }));
    setCustomFieldDraft({ label: "", value: "" });
  }

  function updateCustomField(key, value) {
    setForm((current) => ({
      ...current,
      customFields: {
        ...(current.customFields || {}),
        [key]: value,
      },
    }));
  }

  function deleteCustomField(key) {
    setForm((current) => {
      const nextFields = { ...(current.customFields || {}) };
      delete nextFields[key];
      return { ...current, customFields: nextFields };
    });
  }

  async function saveDropdownValue(label) {
    if (!dropdownEditor || !onAddDropdownOption) return false;
    const saved = await onAddDropdownOption(dropdownEditor.category, label);
    if (saved === false) return false;
    const value = saved.value || saved.label || label;
    setForm((current) => applyCustomerDropdownValue(current, dropdownEditor.category, value));
    setDropdownEditor(null);
    return true;
  }

  async function submitProspect(nextAction = "") {
    if (!canSaveProspect || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError("");
    try {
      const result = await onSave(form, customer?.id, nextAction);
      if (result === false) throw new Error("Save failed");
    } catch {
      setSubmitError("We could not save this prospect right now. Please review the details and try again.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    await submitProspect();
  }

  return (
    <Modal label={customer ? "Edit prospect form" : "Add prospect form"} onBack={onBack} backLabel={backLabel}>
      <form onSubmit={handleSubmit}>
        <h2>{customer ? "Edit Prospect" : "Add Prospect"}</h2>
        <p className="subtle">Keep customer data structured. Use notes only for context.</p>
        {duplicate ? <div className="notice">Possible duplicate: {duplicate.name} already uses this phone number.</div> : null}
        {currentOwner?.active === false ? <div className="notice">Current owner is inactive. Reassign this prospect to an active sales executive.</div> : null}
        {!hasActiveAssignableRep ? <div className="notice">No active sales reps are available in this reporting scope. Add or reactivate a sales rep from Admin before saving a prospect.</div> : null}
        {dropdownEditor ? (
          <InlineDropdownEditor
            categoryLabel={dropdownEditor.label}
            onSave={saveDropdownValue}
            onCancel={() => setDropdownEditor(null)}
          />
        ) : null}
        <div className="form-grid">
          <TextField label="Customer name" value={form.name} required maxLength={140} onChange={(name) => setForm({ ...form, name })} />
          <TextField label="Mobile number" value={form.phone} required maxLength={32} pattern="[0-9+()\\-\\s]{6,32}" onChange={(phone) => setForm({ ...form, phone })} />
          <TextField label="Email address" type="email" value={form.email} maxLength={180} onChange={(email) => setForm({ ...form, email })} />
          <TextField label="Address" value={form.address} required maxLength={280} onChange={(address) => setForm({ ...form, address })} />
          <TextField label="Customer location" value={form.location} onChange={(location) => setForm({ ...form, location })} />
          <SelectField
            label="Customer type"
            value={form.type}
            options={ensureDropdownValue(customerTypeOptions, form.type)}
            actionLabel="Add customer type"
            onAction={onAddDropdownOption ? () => setDropdownEditor({ category: "customerType", label: "Customer type" }) : undefined}
            onChange={(type) => setForm({ ...form, type })}
          />
          <SelectField
            label="Industry type"
            value={form.industryType}
            options={ensureDropdownValue(industryTypeOptions, form.industryType)}
            actionLabel="Add industry type"
            onAction={onAddDropdownOption ? () => setDropdownEditor({ category: "industryType", label: "Industry type" }) : undefined}
            onChange={(industryType) =>
              setForm({
                ...form,
                industryType,
                businessVerticals: shouldSyncBusinessVerticals(form.businessVerticals, form.industryType) ? [industryType] : form.businessVerticals,
              })
            }
          />
          <TextField
            label="Business vertical tags"
            value={formatBusinessVerticals(form.businessVerticals, form.industryType)}
            maxLength={400}
            onChange={(businessVerticals) => setForm({ ...form, businessVerticals })}
          />
          <SelectField
            label="Lead source"
            value={form.leadSource}
            options={ensureDropdownValue(leadSourceOptions, form.leadSource)}
            actionLabel="Add lead source"
            onAction={onAddDropdownOption ? () => setDropdownEditor({ category: "leadSource", label: "Lead source" }) : undefined}
            onChange={(leadSource) => setForm({ ...form, leadSource })}
          />
          <TextField label="Product interest" value={form.productInterest} required maxLength={180} onChange={(productInterest) => setForm({ ...form, productInterest })} />
          <TextField label="Budget" value={form.budget} onChange={(budget) => setForm({ ...form, budget })} />
          <TextField label="Potential budget" value={form.potentialBudget} onChange={(potentialBudget) => setForm({ ...form, potentialBudget })} />
          <TextField label="Expected purchase timeline" value={form.timeline} onChange={(timeline) => setForm({ ...form, timeline })} />
          <TextField label="Decision maker" value={form.decisionMaker} onChange={(decisionMaker) => setForm({ ...form, decisionMaker })} />
          <TextField label="Quantity requirement" value={form.quantityRequirement} onChange={(quantityRequirement) => setForm({ ...form, quantityRequirement })} />
          <SelectField
            label="Pain point"
            value={form.painPoint}
            options={ensureDropdownValue(painPointOptions, form.painPoint)}
            actionLabel="Add pain point"
            onAction={onAddDropdownOption ? () => setDropdownEditor({ category: "painPoint", label: "Pain point" }) : undefined}
            onChange={(painPoint) => setForm({ ...form, painPoint })}
          />
          <TextField label="Competitor comparison" value={form.competitorComparison} onChange={(competitorComparison) => setForm({ ...form, competitorComparison })} />
          <TextField label="Negotiation terms" value={form.negotiationTerms} onChange={(negotiationTerms) => setForm({ ...form, negotiationTerms })} />
          <SelectField
            label="Lead status"
            value={form.status}
            options={ensureDropdownValue(leadStatusOptions, form.status)}
            actionLabel="Add lead status"
            onAction={onAddDropdownOption ? () => setDropdownEditor({ category: "leadStatus", label: "Lead status" }) : undefined}
            onChange={(status) => setForm({ ...form, status })}
          />
          <SelectField
            label="Lifecycle stage"
            value={form.lifecycleStage}
            options={ensureDropdownValue(lifecycleStageOptions, form.lifecycleStage)}
            actionLabel="Add lifecycle stage"
            onAction={onAddDropdownOption ? () => setDropdownEditor({ category: "lifecycleStage", label: "Lifecycle stage" }) : undefined}
            onChange={(lifecycleStage) => setForm({ ...form, lifecycleStage })}
          />
          <TextField label="Lifecycle reason" value={form.lifecycleReason} onChange={(lifecycleReason) => setForm({ ...form, lifecycleReason })} />
          <TextField label="Follow-up date" type="date" value={form.followUpDate} onChange={(followUpDate) => setForm({ ...form, followUpDate })} />
          <TextField label="Latitude" type="number" min="-90" max="90" step="0.000001" value={form.latitude} onChange={(latitude) => setForm({ ...form, latitude })} />
          <TextField label="Longitude" type="number" min="-180" max="180" step="0.000001" value={form.longitude} onChange={(longitude) => setForm({ ...form, longitude })} />
          <TextField label="Geofence radius meters" type="number" min="50" max="10000" value={form.geofenceRadiusMeters} onChange={(geofenceRadiusMeters) => setForm({ ...form, geofenceRadiusMeters })} />
          <TextField label="Credit limit" type="number" min="0" max="100000000" value={form.creditLimit} onChange={(creditLimit) => setForm({ ...form, creditLimit })} />
          <SelectField
            label="Assigned sales executive"
            value={form.ownerId}
            options={displayedOwnerOptions}
            disabled={user.role === "sales" || !hasActiveAssignableRep}
            actionLabel="Onboard employee"
            onAction={user.role === "admin" ? onAddEmployee : undefined}
            secondaryActionLabel="Open selected employee"
            secondaryActionDisabled={!form.ownerId}
            onSecondaryAction={user.role === "admin" && onOpenEmployee ? () => onOpenEmployee(form.ownerId) : undefined}
            onChange={(ownerId) => setForm({ ...form, ownerId })}
          />
          <CheckboxField label="Customer consent / opt-in" checked={form.consentOptIn !== false} onChange={(consentOptIn) => setForm({ ...form, consentOptIn })} />
          <div className="field full">
            <label>Notes / comments</label>
            <textarea aria-label="Notes / comments" maxLength={1000} value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
          </div>
          <div className="field full">
            <label>Additional prospect fields</label>
            <div className="custom-field-controls">
              <input
                aria-label="Custom field name"
                maxLength={80}
                placeholder="Field name, e.g. GSTIN or alternate mobile"
                value={customFieldDraft.label}
                onChange={(event) => setCustomFieldDraft({ ...customFieldDraft, label: event.target.value })}
              />
              <input
                aria-label="Custom field value"
                maxLength={500}
                placeholder="Field value"
                value={customFieldDraft.value}
                onChange={(event) => setCustomFieldDraft({ ...customFieldDraft, value: event.target.value })}
              />
              <button className="secondary-btn" type="button" onClick={addCustomField}>
                Add Field
              </button>
            </div>
            {customFieldEntries.length ? (
              <div className="custom-field-list">
                {customFieldEntries.map(([key, value]) => (
                  <div className="custom-field-row" key={key}>
                    <strong>{key}</strong>
                    <input
                      aria-label={`${key} value`}
                      maxLength={500}
                      value={value}
                      onChange={(event) => updateCustomField(key, event.target.value)}
                    />
                    <button className="mini-btn" type="button" onClick={() => deleteCustomField(key)}>
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        {submitError ? <div className="notice">{submitError}</div> : null}
        <div className="continue-actions">
          <span>Save and continue with this prospect</span>
          <button className="secondary-btn" type="button" disabled={!canSaveProspect || submitting} onClick={() => submitProspect("call")}>
            Save & Call
          </button>
          <button className="secondary-btn" type="button" disabled={!canSaveProspect || submitting} onClick={() => submitProspect("visit")}>
            Save & Visit
          </button>
          <button className="secondary-btn" type="button" disabled={!canSaveProspect || submitting} onClick={() => submitProspect("message")}>
            Save & Message
          </button>
          <button className="secondary-btn" type="button" disabled={!canSaveProspect || submitting || !canDraftOrder} onClick={() => submitProspect("order")}>
            Save & Draft Order
          </button>
        </div>
        <div className="modal-actions">
          <button className="ghost-btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary-btn" type="submit" disabled={!canSaveProspect || submitting}>
            {submitting ? "Saving..." : "Save Prospect"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ActivityModal({ customers, users = [], customer, activityType, draft, businessDate, activityCustomFields = [], dropdownOptions = [], onBack, backLabel, onClose, onSave, onAddCustomer, onOpenCustomer, onAddDropdownOption }) {
  const hasCustomers = customers.length > 0;
  const customerOptions = hasCustomers ? customerOwnerOptions(customers, users) : [{ value: "", label: "No customers available" }];
  const [form, setForm] = useState({
    customerId: customer?.id ?? draft?.customerId ?? customers[0]?.id ?? "",
    type: draft?.type ?? activityType ?? "call",
    date: draft?.date ?? businessDate ?? getLocalCalendarDate(),
    visitMode: draft?.visitMode ?? "logged",
    outcome: draft?.outcome ?? "",
    summary: draft?.summary ?? "",
    latitude: draft?.latitude ?? "",
    longitude: draft?.longitude ?? "",
    customFields: draft?.customFields ?? {},
  });
  const [gpsStatus, setGpsStatus] = useState("Location not captured yet.");
  const [dropdownEditor, setDropdownEditor] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const submittingRef = useRef(false);

  const callOutcomeOptions = dropdownValues(dropdownOptions, "callOutcome", callOutcomes);
  const visitOutcomeOptions = dropdownValues(dropdownOptions, "visitOutcome", visitOutcomes);
  const outcomes = form.type === "visit"
    ? ensureDropdownValue(visitOutcomeOptions, form.outcome)
    : ensureDropdownValue(callOutcomeOptions, form.outcome);
  const outcome = form.outcome || outcomes[0];
  const selectedCustomerHasActiveOwner = hasCustomers && hasActiveCustomerOwner(form.customerId, customers, users);
  const selectedCustomer = customers.find((item) => item.id === form.customerId);
  const visibleCustomFields = activityCustomFields.filter((field) => activityCustomFieldMatches(field, form.type, selectedCustomer));
  const currentDraft = () => ({ ...form, outcome });

  function captureGps() {
    setGpsStatus("Requesting location permission...");
    if (!navigator.geolocation) {
      setGpsStatus("GPS is not available in this browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = String(position.coords.latitude);
        const longitude = String(position.coords.longitude);
        setForm((current) => ({ ...current, latitude, longitude }));
        setGpsStatus(`${Number(latitude).toFixed(5)}, ${Number(longitude).toFixed(5)}`);
      },
      () => setGpsStatus("Location permission denied or unavailable."),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  async function saveDropdownValue(label) {
    if (!dropdownEditor || !onAddDropdownOption) return false;
    const saved = await onAddDropdownOption(dropdownEditor.category, label);
    if (saved === false) return false;
    const value = saved.value || saved.label || label;
    setForm((current) => ({ ...current, outcome: value }));
    setDropdownEditor(null);
    return true;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!selectedCustomerHasActiveOwner || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError("");
    try {
      const result = await onSave({ ...form, outcome, customFields: pickVisibleCustomFieldValues(form.customFields, visibleCustomFields) });
      if (result === false) throw new Error("Save failed");
    } catch {
      setSubmitError("We could not save this activity right now. Please review the details and try again.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Modal label="Log activity form" onBack={onBack} backLabel={backLabel}>
      <form onSubmit={handleSubmit}>
        <h2>Log Activity</h2>
        <p className="subtle">Calls and visits are saved through the REST API. Desktop visit logs are saved as completed history; use the employee app for live field check-in/check-out.</p>
        {!hasCustomers ? <div className="notice">No assigned customers yet. Add or assign a prospect before logging activity.</div> : null}
        {hasCustomers && !selectedCustomerHasActiveOwner ? <div className="notice">Selected customer is assigned to an inactive sales rep. Reassign the prospect before logging activity.</div> : null}
        {dropdownEditor ? (
          <InlineDropdownEditor
            categoryLabel={dropdownEditor.label}
            onSave={saveDropdownValue}
            onCancel={() => setDropdownEditor(null)}
          />
        ) : null}
        <div className="form-grid">
          <SelectField
            label="Customer"
            value={form.customerId}
            options={customerOptions}
            disabled={!hasCustomers}
            actionLabel="Add prospect"
            onAction={onAddCustomer ? () => onAddCustomer(currentDraft()) : undefined}
            secondaryActionLabel="Open selected prospect"
            secondaryActionDisabled={!form.customerId}
            onSecondaryAction={onOpenCustomer ? () => onOpenCustomer(form.customerId, currentDraft()) : undefined}
            onChange={(customerId) => setForm({ ...form, customerId, customFields: {} })}
          />
          <SelectField
            label="Activity type"
            value={form.type}
            options={[
              { value: "call", label: "Call" },
              { value: "visit", label: "Visit" },
            ]}
            onChange={(type) => setForm({ ...form, type, outcome: "", customFields: {} })}
          />
          <TextField label="Date" type="date" value={form.date} required onChange={(date) => setForm({ ...form, date })} />
          <SelectField
            label="Outcome"
            value={outcome}
            options={outcomes}
            actionLabel={`Add ${form.type === "visit" ? "visit outcome" : "call outcome"}`}
            onAction={onAddDropdownOption ? () => setDropdownEditor({
              category: form.type === "visit" ? "visitOutcome" : "callOutcome",
              label: form.type === "visit" ? "Visit outcome" : "Call outcome",
            }) : undefined}
            onChange={(nextOutcome) => setForm({ ...form, outcome: nextOutcome })}
          />
          {form.type === "visit" ? (
            <div className="field full">
              <label>Visit GPS check-in</label>
              <div className="row-actions">
                <button className="secondary-btn" type="button" onClick={captureGps}>
                  Capture GPS
                </button>
                <span className="subtle">{gpsStatus}</span>
              </div>
            </div>
          ) : null}
          {visibleCustomFields.map((field) => (
            <ActivityCustomFieldInput
              key={field.id}
              field={field}
              value={form.customFields[field.key] ?? ""}
              onChange={(value) => setForm({ ...form, customFields: { ...form.customFields, [field.key]: value } })}
            />
          ))}
          <div className="field full">
            <label>Summary</label>
            <textarea aria-label="Summary" maxLength={1000} value={form.summary} onChange={(event) => setForm({ ...form, summary: event.target.value })} />
          </div>
        </div>
        {submitError ? <div className="notice">{submitError}</div> : null}
        <div className="modal-actions">
          <button className="ghost-btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary-btn" type="submit" disabled={!selectedCustomerHasActiveOwner || submitting}>
            {submitting ? "Saving..." : "Save Activity"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ActivityCustomFieldInput({ field, value, onChange }) {
  if (field.fieldType === "select") {
    const options = [
      { value: "", label: field.required ? "Select an option" : "Not specified" },
      ...(field.options || []).map((option) => ({ value: option, label: option })),
    ];
    return <SelectField label={customFieldLabel(field)} value={value} options={options} required={field.required} onChange={onChange} />;
  }
  const inputType = field.fieldType === "number" || field.fieldType === "date" ? field.fieldType : "text";
  return (
    <TextField
      label={customFieldLabel(field)}
      type={inputType}
      value={value}
      required={field.required}
      maxLength={500}
      onChange={onChange}
    />
  );
}

function customFieldLabel(field) {
  return field.required ? `${field.label} *` : field.label;
}

function pickVisibleCustomFieldValues(values, fields) {
  const allowedKeys = new Set(fields.map((field) => field.key));
  return Object.fromEntries(Object.entries(values || {}).filter(([key]) => allowedKeys.has(key)));
}

function activityCustomFieldMatches(field, activityType, customer) {
  if (field.status !== "Approved") return false;
  if (!(field.appliesTo === "all" || field.appliesTo === activityType)) return false;
  if (!scopeIncludes(field.customerTypes, customer?.type)) return false;
  return scopeIncludesAny(field.industryTypes, customer?.businessVerticals || [customer?.industryType]);
}

function scopeIncludes(scopeValues = [], customerValue = "") {
  if (!Array.isArray(scopeValues) || !scopeValues.length) return true;
  return scopeValues.some((value) => value.toLowerCase() === String(customerValue || "").toLowerCase());
}

function scopeIncludesAny(scopeValues = [], customerValues = []) {
  if (!Array.isArray(scopeValues) || !scopeValues.length) return true;
  const values = Array.isArray(customerValues) ? customerValues : [customerValues];
  return values.some((value) => scopeIncludes(scopeValues, value));
}

function InlineDropdownEditor({ categoryLabel, onSave, onCancel }) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const cleanValue = value.trim();

  async function saveValue() {
    if (!cleanValue || saving) return;
    setSaving(true);
    setError("");
    try {
      const saved = await onSave(cleanValue);
      if (saved === false) throw new Error("Save failed");
    } catch {
      setError("We could not add this option right now. Please check the value and try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="notice inline-dropdown-editor">
      <div>
        <strong>Add {categoryLabel}</strong>
        <p>Add the missing option and it will be selected in this form.</p>
      </div>
      <input
        aria-label={`New ${categoryLabel}`}
        maxLength={80}
        value={value}
        onChange={(event) => setValue(event.target.value)}
      />
      <div className="row-actions">
        <button className="mini-btn" type="button" disabled={!cleanValue || saving} onClick={saveValue}>
          {saving ? "Adding..." : "Add Option"}
        </button>
        <button className="mini-btn" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
      {error ? <span className="subtle">{error}</span> : null}
    </div>
  );
}

function applyCustomerDropdownValue(current, category, value) {
  if (category === "customerType") return { ...current, type: value };
  if (category === "industryType") {
    return {
      ...current,
      industryType: value,
      businessVerticals: shouldSyncBusinessVerticals(current.businessVerticals, current.industryType) ? [value] : current.businessVerticals,
    };
  }
  if (category === "leadSource") return { ...current, leadSource: value };
  if (category === "painPoint") return { ...current, painPoint: value };
  if (category === "leadStatus") return { ...current, status: value };
  if (category === "lifecycleStage") return { ...current, lifecycleStage: value };
  return current;
}

function formatBusinessVerticals(value, fallback = "") {
  if (Array.isArray(value)) return value.join(", ");
  return value || fallback || "";
}

function shouldSyncBusinessVerticals(value, currentIndustryType = "") {
  if (Array.isArray(value)) return value.length <= 1 && (!value[0] || value[0] === currentIndustryType);
  return !String(value || "").trim() || String(value).trim() === currentIndustryType;
}
